import type { ReactNode } from 'react';
// Los catalogos configurables, cargados del servidor UNA vez y compartidos.
//
// HU-02 y HU-30: "el administrador puede agregar el valor sin que se redespliegue el
// sistema". Por eso no son constantes del front. Y se cargan una sola vez, arriba,
// porque el formulario los pide desde ocho secciones distintas: pedirlos por
// componente serian ocho peticiones identicas cada vez que se abre un caso.
//
// El formulario los consume con la MISMA forma que tenian en el prototipo
// (`CATALOGOS.roles`, `CATALOGOS.especies`...), asi que la pantalla validada no
// cambia: lo que cambia es de donde salen.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  CATALOGO,
  catalogosDelFormulario,
  configuracionDelFormulario,
  listarCategoriasDeEvento,
  listarPaises,
} from '../api/catalogos';
import type { CategoriaDeEvento, RespuestaDeCatalogo } from '../api/catalogos';
import type { RespuestaDelApi } from '../tipos';

// ---------------------------------------------------------------------------------
// LO QUE LLEGA DEL SERVIDOR Y LO QUE CONSUMEN LAS PANTALLAS
//
// Son dos formas distintas a proposito: la respuesta trae los nombres del backend
// (`codigo`, `exige_profesional_responsable`) y las pantallas leen los que ya tenian
// (`cod`, `firmaPRFV`). La traduccion vive en este adaptador para no tocar pantallas
// ya validadas por QA.
// ---------------------------------------------------------------------------------

/** Un valor de catalogo, tal como responde `/referencias/catalogos/{c}`. */
type ValorDeCatalogo = { codigo: string; nombre?: string; activo?: boolean };

/** Una opcion ya lista para un `Select` de formulario. */
export type OpcionDeFormulario = { id: string; label: string };

/** Una categoria de evento adverso, con la marca de dato sensible que decide RN-09. */
export type CategoriaDelFormulario = { id: string; label: string; l2: boolean };

/** Un pais con su autoridad y su ruta de firma (RN-05). */
export type PaisDelFormulario = {
  cod: string;
  nombre: string;
  autoridad: string;
  firmaPRFV?: boolean;
  /**
   * CC-02 (CC2-01-C1): las autoridades ACTIVAS del país unidas con «·» («SENASA · SANIPES»), la
   * predeterminada primero. Es la pista del selector de país del registro; con una sola autoridad
   * (o un servidor anterior a CC-02 sin `autoridades[]`) es la `autoridad` de siempre.
   */
  pistaDeAutoridades: string;
  /**
   * CC-02 (B10): las autoridades del país, activas o no, en su orden (la predeterminada
   * primero). Las usa el filtro de autoridad de los indicadores, que mide también cartas ya
   * enviadas a una autoridad que después se desactivó. Vacío con un servidor anterior.
   */
  autoridades?: { codigo: string; nombre: string }[];
  /**
   * RN-82: la zona IANA del país (`parametro_pais.zona_horaria`), en la que se toma el día de
   * cada instante de sus casos. `null` sin configurar o con un servidor anterior que no la manda:
   * quien formatea usa entonces `America/Lima`, el mismo respaldo que el servidor.
   */
  zonaHoraria: string | null;
};

// El maestro de productos YA NO vive aqui (HU-29, tarea 29-6): es por pais y lo pide el
// registro con el pais del borrador (`lib/useMaestroDelPais.ts`).

/** Lo que `useCatalogos()` entrega. Se deriva de lo que arma el proveedor. */
export type Catalogos = {
  cargando: boolean;
  faltanPaises: boolean;
  /** Topes por campo de HU-31. Vacio = sin configuracion, y entonces no se recorta nada. */
  LIMITES: Record<string, number>;
  /**
   * AJ-EP (918-4): el intervalo de autoguardado del parametro `autoguardado_intervalo_segundos`, en
   * ms. `null` mientras `/referencias/configuracion-del-formulario` no lo traiga (hoy no lo trae).
   */
  AUTOGUARDADO_MS: number | null;
  CORREO: { desechables: string[]; frecuentes: string[] };
  PAISES: PaisDelFormulario[];
  CATALOGOS: {
    especies: OpcionDeFormulario[];
    roles: OpcionDeFormulario[];
    estadoFisiologico: OpcionDeFormulario[];
    categoriaEA: CategoriaDelFormulario[];
    viaExposicionPersonas: OpcionDeFormulario[];
    sitioInyeccion: OpcionDeFormulario[];
    desenlaces: OpcionDeFormulario[];
    criteriosPreliminares: OpcionDeFormulario[];
    /** Cerrados por el Formato Verde, no configurables. */
    sexo: string[];
    sexoPersona: string[];
    unidadTiempo: { value: string; label: string }[];
  };
};

const Contexto = createContext<Catalogos | null>(null);

// `GET /referencias/catalogos/{c}` devuelve `{catalogo, valores: [...]}`, NO una
// lista suelta. Leerlo como lista hacia que `.filter` se llamara sobre un objeto y
// reventara el proveedor entero: TODOS los desplegables del formulario, de golpe.
const valoresDe = (respuesta: { valores?: ValorDeCatalogo[] } | null | undefined): ValorDeCatalogo[] => respuesta?.valores ?? [];

// (Aqui vivia `nombres()`, que devolvia la ETIQUETA suelta. Se elimino el
// 27-ago-2026: sus tres consumidores eran columnas FK y guardar la etiqueta
// rompia la clave foranea — todos los catalogos del formulario van como
// {id, label} desde entonces.)

// Los catalogos de la seccion 7 se consumen como {id, label} y NO como nombres
// sueltos: lo que se guarda en `afectacion_via_exposicion.via` y en
// `afectacion_persona.sitio_inyeccion` son FK al CODIGO del catalogo. Mandar la
// etiqueta ('Exposicion por inyeccion') viola la FK y el caso no se guarda.
const opciones = (respuesta: { valores?: ValorDeCatalogo[] } | null | undefined): OpcionDeFormulario[] =>
  valoresDe(respuesta)
    .filter((x) => x.activo !== false)
    .map((x) => ({ id: x.codigo, label: x.nombre ?? x.codigo }));

// Las categorias de la seccion 3 NO vienen de `/catalogos/{c}`: llegan de
// `/referencias/categorias-de-evento`, que devuelve una LISTA suelta (no `{valores}`)
// con `activa_dato_l2` por categoria. Pedirlas por la ruta generica devolvia error
// —el backend excluye `categoria_ea` de los catalogos mantenibles a proposito— y el
// `.catch(() => [])` lo tragaba en silencio: la seccion se pintaba sin una sola
// casilla y el bloque L2 no habia forma de habilitarlo.
const aCategoriaDelFormulario = (c: { codigo: string; nombre: string; activa_dato_l2?: boolean }): CategoriaDelFormulario => ({
  id: c.codigo,
  label: c.nombre,
  // RN-09 la decide la base (CHECK `categoria_l2_es_solo_la_de_personas`) y viaja en
  // la respuesta. Se lee de ahi y NO se compara contra un codigo escrito en el front:
  // esa copia de la regla es justo la que se queda atras.
  l2: c.activa_dato_l2 === true,
});

// Los paises YA NO son una constante del front. Vivian en `src/data/paises.js` con su
// autoridad al lado, y la base tenia las mismas dos tablas —`pais` y `parametro_pais`—
// sin que nadie las leyera: editar `pais.nombre` en la BD no cambiaba el desplegable.
//
// El API los devuelve con los nombres de la base (`codigo`,
// `exige_profesional_responsable`) y las pantallas leen `cod` y `firmaPRFV`, que es la
// forma que tenian en el prototipo. La traduccion vive aqui, en el adaptador, igual
// que la del maestro de productos: asi la pantalla validada no se toca.
// CC-02 (AT S3 §11.5.2): `autoridades[]` llega en el orden del país (la predeterminada primero).
// Solo las activas: una desactivada ya no recibe cartas nuevas (RN-72).
const pistaDeAutoridades = (p: RespuestaDelApi): string => {
  const activas = ((p.autoridades ?? []) as { nombre: string; activa: boolean }[])
    .filter((a) => a.activa)
    .map((a) => a.nombre);
  return activas.length ? activas.join(' · ') : (p.autoridad ?? '');
};

const aPaisDelFormulario = (p: RespuestaDelApi): PaisDelFormulario => ({
  cod: p.codigo,
  nombre: p.nombre,
  // `null` cuando el pais no esta parametrizado en `parametro_pais`. Se deja pasar tal
  // cual —el `Select` no pinta el `hint` vacio— en vez de inventar una autoridad.
  autoridad: p.autoridad ?? '',
  // RN-05, la ruta de firma. Llega de la base y no se deduce del codigo del pais: el
  // front lo tenia escrito como `firmaPRFV: false` junto a `cod: 'PE'`, y el dia que
  // Peru exija PRFV eso obligaria a redesplegar la SPA para cumplir una norma.
  firmaPRFV: p.exige_profesional_responsable,
  pistaDeAutoridades: pistaDeAutoridades(p),
  autoridades: ((p.autoridades ?? []) as { codigo: string; nombre: string }[])
    .map((a) => ({ codigo: a.codigo, nombre: a.nombre })),
  zonaHoraria: typeof p.zona_horaria === 'string' && p.zona_horaria ? p.zona_horaria : null,
});

export function ProveedorDeCatalogos({ children }: { children?: ReactNode }) {
  const [crudos, setCrudos] = useState<Record<string, RespuestaDeCatalogo> | null>(null);
  const [categorias, setCategorias] = useState<CategoriaDeEvento[]>([]);
  const [paises, setPaises] = useState<PaisDelFormulario[]>([]);
  const [configuracion, setConfiguracion] = useState<RespuestaDelApi | null>(null);
  const [faltanPaises, setFaltanPaises] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    (async () => {
      const listas = await catalogosDelFormulario();
      if (vigente) setCrudos(listas);
      try {
        const cats = await listarCategoriasDeEvento();
        if (vigente) setCategorias((cats ?? []).filter((c) => c.activo !== false));
      } catch (error) {
        // Sin categorias no hay seccion 3. Se deja constancia en consola en vez de
        // fallar en silencio, que es como este mismo fallo paso desapercibido.
        console.error('No se pudieron cargar las categorias de evento (seccion 3).', error);
      }
      try {
        const lista = await listarPaises();
        if (vigente) setPaises((lista ?? []).map(aPaisDelFormulario));
      } catch (error) {
        // NO se cae de vuelta a una lista escrita aqui, y es deliberado: ese respaldo
        // es exactamente el defecto que este cambio cierra —volveria a haber dos
        // copias, y la copia solo se leeria cuando nadie esta mirando—. Sin paises no
        // se puede registrar un caso (el pais decide autoridad, plazo y correlativo),
        // asi que la seccion 1 lo dice en pantalla en vez de ofrecer una lista que
        // quiza ya no coincide con la base.
        if (vigente) setFaltanPaises(true);
        console.error('No se pudo cargar la lista de paises (seccion 1).', error);
      }
      try {
        const cfg = await configuracionDelFormulario();
        if (vigente) setConfiguracion(cfg);
      } catch (error) {
        // SIN TOPES, PERO CON FORMULARIO. Es lo contrario de lo que se hace con los
        // paises o el maestro de SKU, y la diferencia esta razonada: sin pais no se
        // puede registrar un caso, mientras que sin topes solo se puede escribir de mas
        // en un campo de texto. Bloquear la pantalla por eso seria desproporcionado; lo
        // que no se hace es callarselo.
        console.error(
          'No se pudieron cargar los limites del formulario (HU-31). Los campos '
          + 'quedan sin tope de longitud hasta que el parametro este disponible.',
          error,
        );
      }
      if (vigente) setCargando(false);
    })();
    return () => {
      vigente = false;
    };
  }, []);

  const valor = useMemo<Catalogos>(() => {
    const c = crudos ?? {};
    return {
      cargando,
      faltanPaises,
      // Los topes de cada casilla, `{campo: numero}`. Vacio = sin configuracion; los
      // ayudantes de `entradas.js` tratan `undefined` como "sin tope", que es como se
      // comportaba el formulario antes de que esto existiera.
      LIMITES: configuracion?.limites ?? {},
      AUTOGUARDADO_MS: typeof configuracion?.autoguardado_intervalo_segundos === 'number' && configuracion.autoguardado_intervalo_segundos > 0
        ? configuracion.autoguardado_intervalo_segundos * 1000
        : null,
      // Las dos listas de dominios que usa la validacion del correo.
      CORREO: {
        desechables: configuracion?.correos_desechables ?? [],
        frecuentes: configuracion?.correos_frecuentes ?? [],
      },
      PAISES: paises,
      CATALOGOS: {
        // {id, label} como desenlaces/sitioInyeccion, y POR LO MISMO: las tres
        // columnas son FK a su catalogo (`especie`, `profesion_o_rol`,
        // `estado_fisiologico`). Con `nombres()` el Select guardaba la ETIQUETA
        // ("Bovinos", "Veterinario") y todo guardado moria en 422
        // codigo_de_catalogo_inexistente en cuanto el esquema dejo de taparlo
        // (27-ago-2026, tras corregir animales[].orden).
        especies: opciones(c[CATALOGO.ESPECIE]),
        roles: opciones(c[CATALOGO.PROFESION_O_ROL]),
        estadoFisiologico: opciones(c[CATALOGO.ESTADO_FISIOLOGICO]),
        categoriaEA: categorias.map(aCategoriaDelFormulario),
        viaExposicionPersonas: opciones(c[CATALOGO.VIA_EXPOSICION_PERSONA]),
        sitioInyeccion: opciones(c[CATALOGO.SITIO_INYECCION]),
        // Como el sitio de inyeccion: se guarda el CODIGO, que es FK a
        // `desenlace_evento`. Guardar la etiqueta rompe la FK y el caso no llega a base.
        desenlaces: opciones(c[CATALOGO.DESENLACE_EVENTO]),
        // LAS TRES respuestas salen de aqui, "Prefiero no opinar" incluida (decision del
        // operador, 25-ago-2026). La pantalla no tiene ningun boton fijo: pinta lo que el
        // catalogo diga, que es lo que permite agregar una cuarta respuesta sin tocarla.
        criteriosPreliminares: opciones(c[CATALOGO.CRITERIO_PRELIMINAR]),
        // Cerrados por el Formato Verde, no configurables.
        sexo: ['macho', 'hembra', 'mixto', 'desconocido'],
        sexoPersona: ['masculino', 'femenino'],
        // {value, label}: el dominio valida el CODIGO sin tilde ("'días' no es una
        // unidad de duracion valida. Opciones: dias, horas, minutos" — 28-ago-2026);
        // la tilde vive solo en la etiqueta. Mismo mal y misma cura que especies/roles.
        unidadTiempo: [
          { value: 'minutos', label: 'minutos' },
          { value: 'horas', label: 'horas' },
          { value: 'dias', label: 'días' },
        ],
      },
    };
  }, [crudos, categorias, paises, configuracion, faltanPaises, cargando]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useCatalogos() {
  const valor = useContext(Contexto);
  if (!valor) {
    throw new Error('useCatalogos necesita <ProveedorDeCatalogos> por encima.');
  }
  return valor;
}

/**
 * Lo mismo, pero devuelve `null` sin proveedor en vez de romper. Para una pantalla que
 * SABE vivir sin catalogos y solo quiere aprovecharlos si los hay —la evaluacion del
 * Comite usa asi la configuracion de correos de HU-31 para validar el del especialista
 * invitado igual que «Registrar caso» (22-sep-2026)—. No lo uses para esquivar el
 * proveedor donde los catalogos hacen falta de verdad: ahi el error es la senal correcta.
 */
export function useCatalogosOpcional() {
  return useContext(Contexto);
}

/**
 * RN-82 (Bug #1078): la zona horaria de un país, para tomar en ella el día de un instante de
 * su caso con `comoFechaDeInstante(valor, zonaDe(pais))`.
 *
 * Devuelve `null` cuando no la sabe (sin proveedor, catálogo aún cargando, país sin zona o
 * desconocido) y NO un valor inventado: el respaldo `America/Lima` lo aplica el formateador, en
 * un solo sitio y con la misma regla que el servidor. Funciona sin proveedor a propósito: las
 * pruebas de una pantalla suelta no montan los catálogos y ahí el día se lee en Lima.
 */
export function useZonaDelPais(): (pais: string | null | undefined) => string | null {
  const paises = useContext(Contexto)?.PAISES;
  return useCallback(
    (pais: string | null | undefined) => (pais ? paises?.find((p) => p.cod === pais)?.zonaHoraria ?? null : null),
    [paises],
  );
}
